import { InventoryResult } from '@mai-team-amusement/core';
import type { OpenInventoryItem } from '@mai-fixed-asset/contracts';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AssetQueryKey, assetApi } from '../api.js';
import { useAssetT } from '../i18n/index.js';
import { ErrorAlert, Space } from './common.js';

/** Confirms an asset as found where it was expected, in an open count. */
export function useConfirmFound() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (item: OpenInventoryItem) =>
      assetApi.checkItem(item.inventoryId, item.code, { result: InventoryResult.Found }),
    onSuccess: async () => {
      for (const queryKey of [
        AssetQueryKey.MyInventoryItems,
        AssetQueryKey.AssetInventoryItems,
        AssetQueryKey.Inventory,
        AssetQueryKey.Inventories,
        AssetQueryKey.History,
      ]) {
        await queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/** On "My assets": the open counts that include assets the user uses, to confirm them. */
export function MyInventoryBanner() {
  const t = useAssetT();
  const items = useQuery({
    queryKey: AssetQueryKey.MyInventoryItems,
    queryFn: assetApi.myInventoryItems,
  });
  const confirm = useConfirmFound();
  const byCount = new Map<number, OpenInventoryItem[]>();
  for (const item of items.data?.items ?? []) {
    byCount.set(item.inventoryId, [...(byCount.get(item.inventoryId) ?? []), item]);
  }
  if (byCount.size === 0) {
    return null;
  }
  return (
    <Stack spacing={Space.Sm} sx={{ mb: Space.Sm }}>
      <ErrorAlert error={confirm.error} />
      {[...byCount.values()].map((list) => (
        <Alert key={list[0]?.inventoryId} severity="info">
          <AlertTitle>{t('myAssets.inventoryBanner', { title: list[0]?.title ?? '' })}</AlertTitle>
          <List dense disablePadding>
            {list.map((item) => (
              <ListItem
                key={item.code}
                disableGutters
                secondaryAction={
                  item.result === InventoryResult.Found ? (
                    <Chip
                      size="small"
                      color="success"
                      icon={<CheckCircleOutline />}
                      label={t('myAssets.confirmed')}
                    />
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={confirm.isPending}
                      onClick={() => {
                        confirm.mutate(item);
                      }}
                    >
                      {t('myAssets.confirm')}
                    </Button>
                  )
                }
              >
                <ListItemText
                  primary={`${item.code} ${item.name}`}
                  secondary={item.expectedLocation?.name ?? null}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      ))}
    </Stack>
  );
}

/** On the asset page: confirm it in the open counts that include it (e.g. after a QR scan). */
export function AssetInventoryActions({ code }: { code: string }) {
  const t = useAssetT();
  const items = useQuery({
    queryKey: [...AssetQueryKey.AssetInventoryItems, code],
    queryFn: () => assetApi.assetInventoryItems(code),
  });
  const confirm = useConfirmFound();
  const list = items.data?.items ?? [];
  if (list.length === 0) {
    return null;
  }
  return (
    <>
      <ErrorAlert error={confirm.error} />
      {list.map((item) =>
        item.result === InventoryResult.Found ? (
          <Alert key={item.inventoryId} severity="success">
            {t('asset.confirmedIn', { title: item.title })}
          </Alert>
        ) : (
          <Button
            key={item.inventoryId}
            variant="contained"
            size="large"
            startIcon={<CheckCircleOutline />}
            disabled={confirm.isPending}
            onClick={() => {
              confirm.mutate(item);
            }}
          >
            {t('asset.confirmIn', { title: item.title })}
          </Button>
        ),
      )}
    </>
  );
}
